import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Webhook,
  Users,
  Send,
  FileJson,
  Sun,
  Moon,
  LogOut,
  Wifi,
  WifiOff,
  Radio,
  DollarSign,
  Menu,
  Key,
  Activity,
  X,
  HelpCircle,
  Shield,
  Boxes,
  FileText,
  AlertTriangle,
  Bug,
  Lock,
  Code,
  Settings,
  TrendingUp,
  Github,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { TooltipProvider, Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import { useWebSocket, type ConnectionStatus } from '@/lib/websocket';
import { logout } from '@/lib/api';
import { useState, useEffect, createContext, useContext } from 'react';
import { KeyboardHelp, useKeyboardHelp } from '@/components/ui/keyboard-help';
import { GlobalSearch, useGlobalSearch } from '@/components/ui/global-search';
import { SkipToMain } from '@/lib/accessibility';
import { CollapsibleNav, type NavGroup } from '@/components/ui/collapsible-nav';

// Theme context
type Theme = 'light' | 'dark';
const ThemeContext = createContext<{ theme: Theme; toggle: () => void }>({
  theme: 'dark',
  toggle: () => {},
});
export const useTheme = () => useContext(ThemeContext);

// WebSocket context
const WebSocketContext = createContext<ReturnType<typeof useWebSocket> | null>(null);
export const useWS = () => {
  const ctx = useContext(WebSocketContext);
  if (!ctx) throw new Error('useWS must be used within Layout');
  return ctx;
};

// Navigation configuration with dynamic badges
function useNavGroups(securityBadge: { critical: number; high: number; total: number } | null): NavGroup[] {
  return [
    {
      id: 'dashboard',
      type: 'single',
      items: [
        { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
      ],
    },
    {
      id: 'events',
      type: 'group',
      label: 'Events & Webhooks',
      icon: Webhook,
      defaultOpen: false,
      items: [
        { to: '/events', icon: Webhook, label: 'Events' },
        { to: '/aggregates', icon: FileJson, label: 'Aggregates' },
        { to: '/deliveries', icon: Send, label: 'Deliveries' },
      ],
    },
    {
      id: 'security',
      type: 'group',
      label: 'Security',
      icon: Shield,
      defaultOpen: true,
      badge: securityBadge?.critical,
      items: [
        { to: '/security', icon: AlertTriangle, label: 'Overview', badge: securityBadge?.total },
        { to: '/security/finding-delivery', icon: Shield, label: 'Finding Delivery' },
        { to: '/security/dependabot', icon: Bug, label: 'Dependabot' },
        { to: '/security/code-scanning', icon: Code, label: 'Code Scanning' },
        { to: '/security/secrets', icon: Lock, label: 'Secret Scanning' },
        { to: '/security/advisories', icon: FileText, label: 'Advisories' },
        { to: '/security/repositories', icon: Boxes, label: 'Repositories' },
        { to: '/security/trends', icon: TrendingUp, label: 'Trends' },
      ],
    },
    {
      id: 'integrations',
      type: 'group',
      label: 'Integrations',
      icon: Radio,
      defaultOpen: false,
      items: [
        { to: '/subscriptions', icon: Users, label: 'Agent Subscriptions' },
        { to: '/http-subscribers', icon: Radio, label: 'Subscribers' },
        { to: '/webhooks', icon: Radio, label: 'Webhooks' },
      ],
    },
    {
      id: 'management',
      type: 'group',
      label: 'Management',
      icon: Settings,
      defaultOpen: false,
      items: [
        { to: '/github-apps', icon: Github, label: 'GitHub Apps' },
        { to: '/repositories', icon: Boxes, label: 'Repositories' },
        { to: '/keys', icon: Key, label: 'API Keys' },
        { to: '/users', icon: Users, label: 'Users' },
        { to: '/audit', icon: FileText, label: 'Audit Logs' },
        { to: '/costs', icon: DollarSign, label: 'Costs' },
      ],
    },
    {
      id: 'system',
      type: 'single',
      items: [
        { to: '/health', icon: Activity, label: 'Health' },
      ],
    },
  ];
}

const navItems = [
  { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
  { to: '/events', icon: Webhook, label: 'Events' },
  { to: '/aggregates', icon: FileJson, label: 'Aggregates' },
  { to: '/security', icon: AlertTriangle, label: 'Security Monitoring' },
  { to: '/security-advisories', icon: Shield, label: 'Security Advisories' },
  { to: '/repositories', icon: Boxes, label: 'Repository Management' },
  { to: '/subscriptions', icon: Users, label: 'Agent Subscriptions' },
  { to: '/http-subscribers', icon: Radio, label: 'Subscribers' },
  { to: '/deliveries', icon: Send, label: 'Deliveries' },
  { to: '/keys', icon: Key, label: 'API Keys' },
  { to: '/audit', icon: FileText, label: 'Audit Logs' },
  { to: '/webhooks', icon: Radio, label: 'Webhooks' },
  { to: '/costs', icon: DollarSign, label: 'Costs' },
  { to: '/health', icon: Activity, label: 'Health' },
];

function ConnectionIndicator({ status }: { status: ConnectionStatus }) {
  const map = {
    connected: { 
      icon: Wifi, 
      color: 'text-primary', 
      label: 'Connected',
      ringColor: 'ring-primary/30',
      glowColor: 'shadow-[0_0_10px_hsl(216_100%_50%/0.3)]'
    },
    connecting: { 
      icon: Radio, 
      color: 'text-warning animate-pulse', 
      label: 'Connecting...',
      ringColor: 'ring-warning/30',
      glowColor: ''
    },
    disconnected: { 
      icon: WifiOff, 
      color: 'text-destructive', 
      label: 'Disconnected',
      ringColor: 'ring-destructive/30',
      glowColor: ''
    },
  };
  const s = map[status];
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className={cn('flex items-center gap-2 text-xs px-2 py-1.5 rounded-md transition-all', s.color)}>
          <span className={cn('relative flex h-5 w-5 items-center justify-center', s.glowColor)}>
            {status === 'connected' && (
              <span className="absolute inline-flex h-full w-full rounded-full bg-primary opacity-20 animate-ping" />
            )}
            <s.icon className="h-3.5 w-3.5 relative z-10" />
          </span>
          <span className="hidden md:inline font-medium">{s.label}</span>
        </span>
      </TooltipTrigger>
      <TooltipContent>WebSocket: {s.label}</TooltipContent>
    </Tooltip>
  );
}

export default function Layout() {
  const [theme, setTheme] = useState<Theme>(() => {
    if (typeof window !== 'undefined') {
      return (localStorage.getItem('theme') as Theme) || 'dark';
    }
    return 'dark';
  });
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [securityBadge, setSecurityBadge] = useState<{ critical: number; high: number; total: number } | null>(null);
  const ws = useWebSocket();
  const navigate = useNavigate();
  const keyboardHelp = useKeyboardHelp();
  const globalSearch = useGlobalSearch();
  
  // Get navigation groups with current badge data
  const navGroups = useNavGroups(securityBadge);

  useEffect(() => {
    document.documentElement.classList.toggle('dark', theme === 'dark');
    localStorage.setItem('theme', theme);
  }, [theme]);

  // Fetch security badge data
  useEffect(() => {
    const fetchBadge = async () => {
      try {
        const apiKey = localStorage.getItem('auth_token') || '';
        const response = await fetch('/api/v1/security/badge', {
          headers: { Authorization: `Bearer ${apiKey}` },
        });
        if (response.ok) {
          const data = await response.json();
          setSecurityBadge(data);
        }
      } catch (error) {
        console.error('Failed to fetch security badge:', error);
      }
    };

    // Initial fetch
    fetchBadge();

    // Poll every 60 seconds
    const interval = setInterval(fetchBadge, 60000);

    return () => clearInterval(interval);
  }, []);

  const toggle = () => setTheme(t => (t === 'dark' ? 'light' : 'dark'));

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const closeMobileMenu = () => setMobileMenuOpen(false);

  return (
    <ThemeContext.Provider value={{ theme, toggle }}>
      <WebSocketContext.Provider value={ws}>
        <TooltipProvider delayDuration={200}>
          <SkipToMain />
          <div className="flex h-screen bg-background text-foreground">
            {/* Mobile Header */}
            <div 
              className="fixed top-0 left-0 right-0 z-50 flex h-14 items-center justify-between border-b border-border bg-card px-4 md:hidden"
              style={{ backgroundColor: 'hsl(var(--card))' }}
            >
              <div className="flex items-center gap-2.5">
                <Webhook className="h-5 w-5 text-primary" />
                <div className="flex flex-col">
                  <span className="text-sm font-bold tracking-tight">Alteriom</span>
                  <span className="text-[10px] leading-tight text-muted-foreground">
                    Webhook Connector
                  </span>
                </div>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="h-10 w-10"
              >
                {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              </Button>
            </div>

            {/* Mobile Menu Overlay */}
            {mobileMenuOpen && (
              <div
                className="fixed inset-0 z-40 md:hidden"
                style={{ backgroundColor: 'hsl(var(--background))' }}
                onClick={closeMobileMenu}
              />
            )}

            {/* Sidebar */}
            <aside
              className={cn(
                'fixed top-14 left-0 bottom-0 z-40 w-60 flex flex-col border-r border-border bg-card backdrop-blur-sm transition-transform md:static md:top-0 md:translate-x-0',
                mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
              )}
              style={{ backgroundColor: 'hsl(var(--card))' }}
            >
              {/* Brand (desktop only) */}
              <div className="hidden md:flex h-14 items-center gap-2.5 border-b border-border px-5 relative overflow-hidden">
                {/* Gradient background */}
                <div className="absolute inset-0 bg-gradient-to-r from-[hsl(216_100%_50%/0.1)] to-transparent" />
                
                {/* Icon with glow */}
                <div className="relative z-10 p-1.5 rounded-lg bg-gradient-to-br from-[hsl(216_100%_50%)] to-[hsl(196_100%_50%)]">
                  <Webhook className="h-5 w-5 text-white" />
                </div>
                
                <div className="relative z-10 flex flex-col">
                  <span className="text-sm font-bold tracking-tight text-gradient-blue">Alteriom</span>
                  <span className="text-[10px] leading-tight text-muted-foreground">
                    Webhook Connector
                  </span>
                </div>
              </div>

              {/* Nav */}
              <nav className="flex-1 px-3 py-4 overflow-y-auto">
                <CollapsibleNav groups={navGroups} onNavigate={closeMobileMenu} />

                <div className="my-3 h-px bg-border" />

                <a
                  href="/api-docs"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-colors"
                >
                  <FileJson className="h-4 w-4" />
                  API Docs
                </a>
              </nav>

              {/* Footer */}
              <div className="border-t border-border p-3 space-y-2 relative overflow-hidden">
                {/* Subtle gradient background */}
                <div className="absolute inset-0 bg-gradient-to-t from-[hsl(216_100%_50%/0.05)] to-transparent" />
                
                <div className="relative z-10">
                  <ConnectionIndicator status={ws.status} />
                </div>
                
                <div className="relative z-10 flex items-center gap-1">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        onClick={keyboardHelp.open}
                        className="h-8 w-8 hover:bg-primary/10 hover:text-primary transition-all"
                      >
                        <HelpCircle className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="top">Keyboard Shortcuts (?)</TooltipContent>
                  </Tooltip>
                  <Button variant="ghost" size="icon" onClick={toggle} className="h-8 w-8 hover:bg-primary/10 hover:text-primary transition-all">
                    {theme === 'dark' ? (
                      <Sun className="h-4 w-4" />
                    ) : (
                      <Moon className="h-4 w-4" />
                    )}
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={handleLogout}
                    className="h-8 w-8 text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-all"
                  >
                    <LogOut className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </aside>

            {/* Main */}
            <main id="main-content" className="flex-1 overflow-y-auto pt-14 md:pt-0">
              <Outlet />
            </main>
          </div>

          {/* Global Keyboard Help Modal */}
          <KeyboardHelp isOpen={keyboardHelp.isOpen} onClose={keyboardHelp.close} />
          
          {/* Global Search (Ctrl+K) */}
          <GlobalSearch isOpen={globalSearch.isOpen} onClose={globalSearch.close} />
        </TooltipProvider>
      </WebSocketContext.Provider>
    </ThemeContext.Provider>
  );
}
